import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { amiGet, amiPost, Card, Loading, NotYet, num, tsFmt, isoFmt, errMsg } from "@/lib/ami";
import AmiAnalysis from "@/pages/ami/AmiAnalysis";

const INSTS = ["BTC-USDT-SWAP", "BTC-USDT"];
const ALL_BARS = ["1w", "1d", "4h", "1h", "15m", "5m", "1m"];

function CoverageTable({ rows }) {
  return (
    <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="ami-coverage-table">
      <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground"><th className="py-2">Instrument</th><th>Intervall</th><th>Kerzen</th><th>von</th><th>bis</th><th>Lücken</th><th>Vollständig</th><th>Status</th></tr></thead>
      <tbody className="font-mono">{rows.map((r) => (
        <tr key={r.inst + r.bar} className="border-t border-border/60" data-testid={`ami-cov-${r.inst}-${r.bar}`}>
          <td className="py-2 font-sans">{r.inst}</td><td>{r.bar}</td><td>{num(r.count, 0)}</td><td>{tsFmt(r.first_ts)}</td><td>{tsFmt(r.last_ts)}</td>
          <td>{num(r.missing, 0)}</td><td>{r.completeness_pct == null ? "–" : `${num(r.completeness_pct, 2)} %`}{r.complete_back ? " · Anfang erreicht" : ""}</td><td className="font-sans text-xs">{r.status}</td>
        </tr>
      ))}</tbody>
    </table></div>
  );
}

function Jobs({ jobs, onStop }) {
  if (!jobs.length) return <NotYet text="Noch kein Import gestartet." />;
  return (
    <div className="space-y-2" data-testid="ami-jobs">
      {jobs.slice(0, 14).map((j) => (
        <div key={j.id} className="flex flex-wrap items-center justify-between gap-2 text-sm border-b border-border/60 pb-2" data-testid={`ami-job-${j.inst}-${j.bar}`}>
          <span className="font-mono">{j.inst} · {j.bar}</span>
          <span className="text-muted-foreground text-xs">{j.status}{j.phase ? ` (${j.phase})` : ""} · {num(j.inserted, 0)} neu · {num(j.requests, 0)} Abrufe{j.oldest_ts ? ` · bis ${tsFmt(j.oldest_ts)}` : ""}{j.error ? ` · ${j.error}` : ""} · {isoFmt(j.created_at)}</span>
          {["queued", "running"].includes(j.status) && <Button size="sm" variant="ghost" onClick={() => onStop(j.id)} data-testid={`ami-job-stop-${j.bar}`}>Stoppen</Button>}
        </div>
      ))}
    </div>
  );
}

export default function AmiHistory() {
  const qc = useQueryClient();
  const cov = useQuery({ queryKey: ["ami-coverage"], queryFn: () => amiGet("/history/coverage"), refetchInterval: 10000 });
  const jobs = useQuery({ queryKey: ["ami-jobs"], queryFn: () => amiGet("/history/jobs"), refetchInterval: 4000 });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["ami-jobs"] }); qc.invalidateQueries({ queryKey: ["ami-coverage"] }); };
  const start = useMutation({
    mutationFn: (inst) => amiPost("/history/import", { inst, bars: ALL_BARS }),
    onSuccess: () => { toast.success("Import eingereiht"); refresh(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const stop = useMutation({ mutationFn: (id) => amiPost(`/history/jobs/${id}/stop`), onSuccess: refresh, onError: (e) => toast.error(errMsg(e)) });
  return (
    <div className="space-y-6" data-testid="ami-history">
      <Card title="Historische BTC-Datenbank" testid="ami-history-import"
        actions={<div className="flex flex-wrap gap-2">{INSTS.map((i) => (
          <Button key={i} size="sm" onClick={() => start.mutate(i)} disabled={start.isPending} data-testid={`ami-import-${i}`}>{i} komplett importieren</Button>
        ))}</div>}>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">
          Lädt alle verfügbaren OKX-Kerzen (1w bis 1m) so weit zurück wie möglich, in der Reihenfolge grob → fein. Der Import ist fortsetzbar und läuft nach einem Neustart automatisch weiter. Duplikate werden erkannt, ungültige und noch laufende Kerzen nicht gespeichert.
          1-Minuten-Kerzen über alle Jahre sind mehrere Millionen Datensätze – das dauert mehrere Stunden und belegt rund 1–2 GB.
        </p>
      </Card>
      <Card title="Datenbestand & Qualität" testid="ami-coverage">{cov.isLoading ? <Loading /> : cov.isError ? <NotYet text={errMsg(cov.error)} /> : <CoverageTable rows={cov.data} />}</Card>
      <Card title="Import-Jobs" testid="ami-jobs-card">{jobs.data ? <Jobs jobs={jobs.data} onStop={(id) => stop.mutate(id)} /> : <Loading />}</Card>
      <div><div className="overline mb-3">Analyse</div><AmiAnalysis /></div>
    </div>
  );
}
